import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { he, enUS, ru, arSA } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { Cookie, Scale } from "lucide-react";
import eyecontrolLogo from "@/assets/eyecontrol-logo.png";
import { useInputMethod } from "@/contexts/InputMethodContext";
import { InputMethod } from "@/lib/inputMethods";
import { useTranslation } from "@/hooks/useTranslation";

export function StatusBar() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const { inputMethod } = useInputMethod();
  const navigate = useNavigate();
  const { t, lang, isRTL, dir } = useTranslation();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const localeMap = { he, en: enUS, ru, ar: arSA } as Record<string, typeof enUS>;
  const dateLocale = localeMap[lang] || enUS;
  const timeString = format(currentTime, "HH:mm");
  const dayName = format(currentTime, "EEEE", { locale: dateLocale });
  const dateString = format(currentTime, isRTL ? "d/M/yyyy" : "M/d/yyyy", { locale: dateLocale });

  // Multi-language footer labels
  const FOOTER_LABELS: Record<string, Record<string, string>> = {
    terms:   { he: "תנאי שימוש", en: "Terms", ru: "Условия", ar: "الشروط" },
    cookies: { he: "עוגיות", en: "Cookies", ru: "Куки", ar: "ملفات تعريف الارتباط" },
  };
  const footerLabel = (key: string) => FOOTER_LABELS[key]?.[lang] || FOOTER_LABELS[key]?.en || key;

  // Get tracking label
  const getTrackingLabel = () => {
    if (inputMethod === InputMethod.HEAD_TRACKING) return t('headTrackingActive');
    if (inputMethod === InputMethod.GAZE_TRACKING) return t('gazeTrackingActive');
    if (inputMethod === InputMethod.MOUTH_GESTURES) return t('mouthGestureTrackingActive');
    return null;
  };

  const trackingLabel = getTrackingLabel();

  return (
    <div 
      className="flex items-center justify-between px-6 py-3 bg-[#0a3d4f] border-t border-primary/30"
      dir={dir}
    >
        {/* Left section: Time + Date & Day */}
        <div className="flex items-center gap-4">
          {/* Time - Most Prominent */}
          <span className="text-2xl font-bold text-white tracking-wider">
            {timeString}
          </span>
          
          {/* Divider */}
          <div className="w-px h-6 bg-white/20" />
          
          {/* Date & Day */}
          <div className="flex flex-col">
            <span className="text-sm font-medium text-white/90">{dayName}</span>
            <span className="text-xs text-white/60">{dateString}</span>
          </div>
        </div>

        {/* Center section: Status */}
        <div className="flex items-center">
          {/* Tracking indicator */}
          {trackingLabel && (
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/20 border border-green-500/30">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs text-green-400 font-medium">{trackingLabel}</span>
            </div>
          )}
        </div>

        {/* Right section: Utilities + Logo */}
        <div className="flex items-center gap-4 min-w-[180px] justify-end">
          {/* Terms and Conditions */}
          <button
            onClick={() => navigate('/legal')}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-white/10 transition-colors"
            aria-label={footerLabel("terms")}
          >
            <Scale className="w-4 h-4 text-white/70" />
            <span className="text-xs text-white/70">{footerLabel("terms")}</span>
          </button>

          {/* Cookie settings */}
          <button
            onClick={() => navigate('/cookies')}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-white/10 transition-colors"
            aria-label={footerLabel("cookies")}
          >
            <Cookie className="w-4 h-4 text-white/70" />
            <span className="text-xs text-white/70">{footerLabel("cookies")}</span>
          </button>

          {/* Divider */}
          <div className="w-px h-6 bg-white/20" />

          {/* Logo */}
          <div className="flex items-center gap-2">
            {!isRTL && <span className="text-[10px] text-white/40 uppercase tracking-wide leading-none">powered by</span>}
            <img 
              src={eyecontrolLogo} 
              alt="EyeControl" 
              className="h-6 object-contain align-middle"
            />
            {isRTL && <span className="text-[10px] text-white/40 uppercase tracking-wide leading-none">powered by</span>}
        </div>
      </div>
    </div>
  );
}
